import React from 'react';
import { DEV_FEATURES } from '__dev/devFeatures';
import {
  PRODUCT_CODE_DAVID_JONES_PREMIERE,
  PRODUCT_CODE_DAVID_JONES_PRESTIGE,
  DAVID_JONES_FEATURE_PREFIX,
  config,
} from '_config';
import { COUNTRY_CODE_AU } from '_config/countryCodes';
import { isBoolean, mapValues, merge, startsWith, upperFirst } from 'lodash';
import { byCard, getProductInfo, isDJ } from 'utils/byConfig';
import { getSessionStorage } from 'utils/sessionStorage';
import { productCodeToDefaultFeatures } from './defaultValues';

export const sharedFeatures = [
  'maintenanceMessage',
  'auDmsUpload',
  'nzDmsUpload',
  'offerTypeMapping',
  'emailVerificationFallback',
  'mobileConfirmationModal',
  'smsEmailVerificationOptions',
  'defaultPromoCode',
  'auBankStatementOcr',
  'nzBankStatementOcr',
  'auDisclaimer',
  'auOneTap',
  'expensesLibrary',
];

export const jsonFeatures = ['djCampaignBanner'];

export const featuresStorageKey = 'features';

export const getFeatures = () => {
  if (
    DEV_FEATURES.USE_LOCAL_FEATURES || // when turn on USE_LOCAL_FEATURES
    import.meta.env.VITE_LAUNCH_DARKLY_INACTIVE // when unit test running any where
  ) {
    return getTransformedFeatures(
      DEV_FEATURES?.FEATURE_TOGGLE_BOOLEAN_VALUE ?? true,
      DEV_FEATURES?.FEATURE_TOGGLE_OVERRIDES,
    );
  }

  return getSessionStorage('launchDarklyFlags');
};

export function convertToFeatureKey(featureKey) {
  if (sharedFeatures.includes(featureKey)) {
    return featureKey;
  }

  if (isDJ()) {
    return `${DAVID_JONES_FEATURE_PREFIX}${upperFirst(featureKey)}`;
  }

  const prefix = getProductInfo().featureToggleKeyPrefix;
  return `${prefix}${upperFirst(featureKey)}`;
}

export function convertToFeatureName(featureName) {
  const { featureToggleKeyPrefix } = getProductInfo();
  const lengthOfPrefix = featureToggleKeyPrefix.length;

  return startsWith(featureName, featureToggleKeyPrefix) ? featureName.substring(lengthOfPrefix) : featureName;
}

const getTransformedFeatures = (booleanValue = true, except = {}) => {
  const defaultFeatures = getDefaultFeatures();
  const transformedFeatures = mapValues(defaultFeatures, (defaultValue, key) => {
    const isMaintenanceToggle = key.toLowerCase().includes('maintenancepage');
    if (key in except) {
      return except[key];
    }
    if (isBoolean(defaultValue) && !isMaintenanceToggle && key !== 'afsAch' && key.indexOf('Launched') === -1) {
      return booleanValue;
    }
    return defaultValue;
  });
  const storageFeatures = getSessionStorage(featuresStorageKey) || {};
  return merge({}, transformedFeatures, storageFeatures);
};

export function isFeatureOn(feature) {
  return getFeatureValue(feature) === true;
}

export function getFeatureValue(feature) {
  const featureKey = convertToFeatureKey(feature);

  if (getFeatures()) {
    return getFeatures()[featureKey];
  }
  return null;
}

/**
 * Get feature toggle value of current product from Json type
 */
export function getFeatureJsonValue(feature) {
  const features = getFeatures();
  if (features && features[feature]) {
    return getFeatures()[feature][config.productCode];
  }
  return null;
}

export function getRatesAndFees() {
  const features = getFeatures();
  if (isDJ()) {
    if (!config.productCode) {
      // either on page dj-product-comparison or resume-your-application with deprecated params `cardType=dj` without `productCode`
      return {};
    }

    return byCard({
      [PRODUCT_CODE_DAVID_JONES_PRESTIGE]: features.prestigeRatesAndFees,
      [PRODUCT_CODE_DAVID_JONES_PREMIERE]: features.premiereRatesAndFees,
    });
  }

  return getFeatureValue('ratesAndFees');
}

export const isDCLOn = () => config.countryCode === COUNTRY_CODE_AU || isFeatureOn('dynamicCreditLimit');

export const getDefaultFeatures = () => {
  return byCard(productCodeToDefaultFeatures);
};

export const parseFeatureString = (featureString, { key, value }) => {
  if (!featureString) {
    return '';
  }
  const pattern = new RegExp(`{{${key}}}`, 'g');
  const parts = featureString.split(pattern);

  return (
    <>
      {parts.map((part, index) => (
        <>
          {part}
          {index === parts.length - 1 ? null : value}
        </>
      ))}
    </>
  );
};

export const isRequestBalanceTransferFeatureOn = (channel, offerType) => {
  const productSpecificToggle = getFeatureJsonValue('requestBalanceTransfer');
  if (isBoolean(productSpecificToggle)) {
    return productSpecificToggle && offerType === 'BALANCE_TRANSFER';
  }

  if (productSpecificToggle == null) {
    return false;
  }

  const channelSpecificToggle = findChannelSpecificToggle({ channel }, productSpecificToggle);
  if (channelSpecificToggle === 'always') {
    return true;
  }
  return channelSpecificToggle && offerType === 'BALANCE_TRANSFER';
};

export const findChannelSpecificToggle = (applyAndBuyParams, jsonFeatureToggle) => {
  if (jsonFeatureToggle == null || applyAndBuyParams == null) {
    return null;
  }
  if (!jsonFeatureToggle.channels) {
    return jsonFeatureToggle;
  }
  if (Object.keys(jsonFeatureToggle.channels)?.includes(applyAndBuyParams.channel)) {
    return jsonFeatureToggle.channels[applyAndBuyParams.channel];
  }
  return null;
};
